/**
 * Calibration regression conversions between strength and equivalent age (EA).
 *
 * Single source of truth for the strength <-> maturity math. Two trend curves
 * are supported (calibration-curves feature); pre-feature data has no curve
 * type and defaults to logarithmic (legacy formula):
 *   Logarithmic: Strength = A + B * ln(EA)   inverse EA = exp((S - A) / B)
 *   Linear:      Strength = A + B * EA       inverse EA = (S - A) / B
 *
 * The log formulas are ported from the legacy
 * PageBase.getEAFromCalibration()/getStrengthFromCalibration().
 *
 * Used by the calibration chart, the product target editor, and the pour/
 * location target setup. All functions return null when the regression is
 * unusable (A/B missing or B == 0) or the input is out of domain, so callers
 * can fall back to manual entry.
 *
 * NOTE: pass the coefficients matching the curve — (regression_a/b) for 'log',
 * (linear_a/b) for 'linear'. `resolveCurve()` picks the right pair from an
 * API object carrying both.
 */

export type CurveType = 'log' | 'linear';

const round1 = (n: number): number => Math.round(n * 10) / 10;

export const normalizeCurveType = (curveType: string | null | undefined): CurveType =>
  curveType === 'linear' ? 'linear' : 'log';

interface CurveFields {
  curve_type?: string | null;
  regression_a?: number | null;
  regression_b?: number | null;
  linear_a?: number | null;
  linear_b?: number | null;
}

/**
 * Pick (curveType, a, b) from an API object carrying both fits. Falls back to
 * the logarithmic coefficients when linear is selected but never computed
 * (mirrors backend curve_math.resolve_curve).
 */
export const resolveCurve = (
  obj: CurveFields | null | undefined,
): { curveType: CurveType; a: number | null; b: number | null } => {
  if (!obj) return { curveType: 'log', a: null, b: null };
  const curveType = normalizeCurveType(obj.curve_type);
  if (curveType === 'linear' && obj.linear_a != null && obj.linear_b != null) {
    return { curveType: 'linear', a: obj.linear_a, b: obj.linear_b };
  }
  return { curveType: 'log', a: obj.regression_a ?? null, b: obj.regression_b ?? null };
};

/**
 * EA (°C-hr) for a given strength via the curve's inverse. Rounded to 1 dp to
 * match the stored target value (parity with the backend ea_from_strength and
 * the legacy getEAFromCalibration "0.0" formatting). Non-positive EA results
 * are unusable and return null.
 */
export const eaFromStrength = (
  strength: number,
  a: number | null | undefined,
  b: number | null | undefined,
  curveType: CurveType = 'log',
): number | null => {
  if (a == null || b == null || b === 0 || !Number.isFinite(strength)) return null;
  const ea = curveType === 'linear' ? (strength - a) / b : Math.exp((strength - a) / b);
  return Number.isFinite(ea) && ea > 0 ? round1(ea) : null;
};

/**
 * Strength for a given EA (°C-hr) via the chosen curve. Returns the raw value;
 * callers format for display (chart uses 2 dp, target readouts 1 dp) —
 * matches the legacy getStrengthFromCalibration.
 */
export const strengthFromEa = (
  ea: number,
  a: number | null | undefined,
  b: number | null | undefined,
  curveType: CurveType = 'log',
): number | null => {
  if (a == null || b == null || !Number.isFinite(ea) || ea <= 0) return null;
  const strength = curveType === 'linear' ? a + b * ea : a + b * Math.log(ea);
  return Number.isFinite(strength) ? strength : null;
};

/** Display R-value: R = sqrt(R²), the legacy report's "rValue". */
export const rValue = (r2: number | null | undefined): number | null =>
  r2 == null ? null : Math.sqrt(Math.max(0, r2));

/** Applied strength = base * (1 + margin/100), rounded to 1 dp (legacy formula). */
export const applySafetyMargin = (baseStrength: number, safetyMarginPct: number): number =>
  round1(baseStrength * (1 + safetyMarginPct / 100));
